import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import MoneyToday from '../components/MoneyToday';
import { toast } from 'react-toastify';
import { RefreshCw, TrendingUp, TrendingDown, DollarSign, AlertCircle, Search, Settings as SettingsIcon } from 'lucide-react';
import { businessToday, isoPlusDays } from '../utils/time';
import { useReadOnly } from '../hooks/useReadOnly';

const fmtDollars = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const fmtWeek = (iso: string) => {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

type WeekRow = { week: string; income: number; expenses: number };

// ── Tiny bar chart ─────────────────────────────────────────────────────────────
// In and out sit SIDE BY SIDE off one baseline, both on the same scale. The
// earlier version stacked them in a fixed-height column and drew "out" at half
// scale, so a week's expenses read as half what they were and a big income week
// pushed the other bar out of the box entirely.
function BarChart({ weeks }: { weeks: WeekRow[] }) {
  const shown = weeks.slice(-12);
  if (!shown.length) return null;
  const maxVal = Math.max(...shown.map(w => Math.max(w.income, w.expenses)), 1);
  // A non-zero week must never draw as nothing — floor it at a visible sliver.
  const h = (v: number) => (v > 0 ? `${Math.max((v / maxVal) * 100, 1.5)}%` : '0%');

  return (
    <div className="mt-2 flex gap-2">
      <div className="w-14 shrink-0 h-32 flex flex-col justify-between items-end text-[10px] text-gray-500 leading-none">
        <span>{fmtDollars(maxVal)}</span>
        <span>{fmtDollars(maxVal / 2)}</span>
        <span>$0</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-end gap-1 h-32 border-l border-b border-gray-200 pl-1 pb-px">
          {shown.map(w => (
            <div key={w.week} className="flex-1 h-full flex items-end justify-center gap-[3px] group relative">
              <div
                className="w-1/2 max-w-[18px] rounded-t bg-green-400 group-hover:bg-green-500 transition-colors"
                style={{ height: h(w.income) }}
                title={`In: ${fmtDollars(w.income)}`}
              />
              <div
                className="w-1/2 max-w-[18px] rounded-t bg-red-300 group-hover:bg-red-400 transition-colors"
                style={{ height: h(w.expenses) }}
                title={`Out: ${fmtDollars(w.expenses)}`}
              />
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-10 hidden group-hover:block
                              bg-gray-900 text-white text-[10px] rounded px-2 py-1 whitespace-nowrap shadow-lg pointer-events-none">
                Week of {fmtWeek(w.week)}<br />
                In: {fmtDollars(w.income)}<br />
                Out: {fmtDollars(w.expenses)}<br />
                Net: <span className={w.income - w.expenses >= 0 ? 'text-green-300' : 'text-red-300'}>{fmtDollars(w.income - w.expenses)}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-1 pl-1 mt-1">
          {shown.map(w => (
            <span key={w.week} className="flex-1 text-[9px] text-gray-500 text-center truncate">{fmtWeek(w.week)}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// The server builds `weeks` from bank transactions only. With no bank connected
// the card used to claim "showing paid invoices" over an empty chart and $0
// totals — the invoice payments it names were never bucketed. Do it here.
function weeksFromInvoices(rows: any[]): WeekRow[] {
  const byWeek: Record<string, WeekRow> = {};
  for (const row of rows || []) {
    const iso = String(row?.date || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) continue;
    const d = new Date(iso + 'T12:00:00');
    d.setDate(d.getDate() - d.getDay());          // Sunday start, same as the server
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (!byWeek[key]) byWeek[key] = { week: key, income: 0, expenses: 0 };
    byWeek[key].income += Number(row.amount) || 0;
  }
  return Object.values(byWeek)
    .map(w => ({ ...w, income: Math.round(w.income * 100) / 100 }))
    .sort((a, b) => a.week.localeCompare(b.week));
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CashFlow() {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [days, setDays] = useState(90);
  // "Custom" = an explicit from/to range instead of a preset number of days.
  const [custom, setCustom] = useState(false);
  const [rangeStart, setRangeStart] = useState(() => isoPlusDays(businessToday(), -90));
  const [rangeEnd, setRangeEnd] = useState(() => businessToday());
  const [txSearch, setTxSearch] = useState('');
  const [txKind, setTxKind] = useState<'all' | 'in' | 'out'>('all');

  const { data: status } = useQuery(
    'banking-status',
    () => api.get('/banking/status').then(r => r.data),
    { retry: false }
  );

  const { data: cashflow, isLoading, isError, error, refetch, isFetching } = useQuery(
    ['cashflow', custom ? `${rangeStart}..${rangeEnd}` : days],
    () => api.get('/banking/cashflow', { params: custom ? { start: rangeStart, end: rangeEnd } : { days } }).then(r => r.data),
    { enabled: true, keepPreviousData: true }
  );
  const fmtD = (iso?: string) => iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const rangeLabel = custom ? `${fmtD(cashflow?.since || rangeStart)} – ${fmtD(cashflow?.until || rangeEnd)}` : `last ${days} days`;

  const sync = useMutation(
    () => api.post('/banking/sync'),
    {
      onSuccess: (r: any) => {
        toast.success(r.data?.message || 'Synced');
        qc.invalidateQueries(['cashflow']);
        qc.invalidateQueries('banking-status');
      },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Sync failed'); },
    }
  );

  const hasBankData   = cashflow?.has_bank_data;
  // Bank transactions when there are any; otherwise the paid invoices the card
  // says it is showing. Totals and chart read the same series either way.
  const weeks: WeekRow[] = hasBankData
    ? (cashflow?.weeks || []).map((w: any) => ({ week: w.week, income: Number(w.income) || 0, expenses: Number(w.expenses) || 0 }))
    : weeksFromInvoices(cashflow?.invoice_income || []);
  const totalIn       = weeks.reduce((s, w) => s + w.income,   0);
  const totalOut      = weeks.reduce((s, w) => s + w.expenses, 0);
  const net           = totalIn - totalOut;
  const allTx: any[]  = cashflow?.transactions || [];
  const q = txSearch.trim().toLowerCase();
  const txRows = allTx.filter((t: any) =>
    (txKind === 'all' || (txKind === 'in' ? Number(t.amount) < 0 : Number(t.amount) > 0))
    && (!q || [t.name, t.raw_name, t.category, String(t.amount), t.date].filter(Boolean).some((v: any) => String(v).toLowerCase().includes(q)))
  );
  const catLabel = (c: string) => String(c || 'other').toLowerCase().replace(/_/g, ' ');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Cash Flow</h1>
          <p className="text-sm text-gray-500 mt-0.5">Money in and out of the bank, week by week</p>
        </div>
        <div className="flex items-center gap-2">
          {status?.connections?.length > 0 && (
            <button onClick={() => sync.mutate()} disabled={sync.isLoading || readOnly} className="btn-secondary text-sm disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${sync.isLoading ? 'animate-spin' : ''}`} />
              {sync.isLoading ? 'Syncing…' : 'Sync now'}
            </button>
          )}
        </div>
      </div>

      {/* How much money do I have — this morning (services/money). */}
      <MoneyToday compact />

      {/* Bank connection — managed in Settings; a one-line status here */}
      {status && (
        <div className="card px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          {!status.configured ? (
            <span className="text-gray-600">Bank sync isn't set up yet.</span>
          ) : status.connections?.length === 0 ? (
            <span className="text-gray-600">No bank connected yet.</span>
          ) : status.connections.map((c: any) => (
            <span key={c.id} className="inline-flex items-center gap-1.5 text-gray-700">
              🏦 <span className="font-medium">{c.institution_name}</span>
              <span className="text-gray-500">· {c.transaction_count ?? 0} transactions{c.last_synced_at ? ` · synced ${new Date(String(c.last_synced_at).replace(' ', 'T') + (String(c.last_synced_at).includes('+') ? '' : 'Z')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}</span>
            </span>
          ))}
          <Link to="/settings#bank-accounts" className="ml-auto text-xs font-medium text-brand-600 hover:text-brand-800 flex items-center gap-1"><SettingsIcon className="h-3.5 w-3.5" /> Manage in Settings</Link>
        </div>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Money In',  value: totalIn,  icon: TrendingUp,   color: 'text-green-600' },
          { label: 'Money Out', value: totalOut, icon: TrendingDown, color: 'text-red-600'   },
          { label: 'Net',       value: net,      icon: DollarSign,   color: net >= 0 ? 'text-green-700' : 'text-red-700' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="card p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon className={`h-4 w-4 ${color}`} />
              <p className="text-xs font-medium text-gray-500">{label}</p>
            </div>
            <p className={`text-xl font-bold ${isError ? 'text-gray-300' : color}`}>{isError ? '—' : fmtDollars(value)}</p>
            <p className="text-xs text-gray-500 mt-0.5">{isError ? 'unavailable' : rangeLabel}</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-black uppercase tracking-tight text-gray-900">Revenue vs Expenses</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {hasBankData ? 'From your bank transactions' : 'Showing paid invoices — connect bank for full picture'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <span className="w-3 h-3 rounded bg-green-400 inline-block" /> In
              <span className="w-3 h-3 rounded bg-red-300 inline-block ml-2" /> Out
            </div>
            <select
              value={custom ? 'custom' : String(days)}
              onChange={e => { if (e.target.value === 'custom') setCustom(true); else { setCustom(false); setDays(Number(e.target.value)); } }}
              className="text-xs border border-gray-300 rounded px-2 py-1"
            >
              <option value="30">30 days</option>
              <option value="90">90 days</option>
              <option value="180">6 months</option>
              <option value="365">1 year</option>
              <option value="custom">Custom range…</option>
            </select>
            {custom && (
              <div className="flex items-center gap-1 text-xs">
                <input type="date" value={rangeStart} max={rangeEnd} onChange={e => setRangeStart(e.target.value)} className="input py-1 text-xs w-auto" />
                <span className="text-gray-500">to</span>
                <input type="date" value={rangeEnd} min={rangeStart} onChange={e => setRangeEnd(e.target.value)} className="input py-1 text-xs w-auto" />
              </div>
            )}
          </div>
        </div>

        {isError ? (
          // An empty chart used to look identical to a failed request. Say which.
          <div className="h-32 flex flex-col items-center justify-center text-sm gap-2">
            <AlertCircle className="h-6 w-6 text-red-400" />
            <p className="text-red-700">Couldn't load cash flow — {(error as any)?.response?.data?.message || (error as any)?.message || 'the server did not respond'}</p>
            <button onClick={() => refetch()} disabled={isFetching} className="btn-secondary text-xs">
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} /> Try again
            </button>
          </div>
        ) : isLoading ? (
          <div className="h-32 flex items-center justify-center text-gray-500 text-sm">Loading…</div>
        ) : weeks.length === 0 ? (
          <div className="h-32 flex flex-col items-center justify-center text-gray-500 text-sm gap-2">
            <AlertCircle className="h-6 w-6 text-gray-300" />
            <p>No money moved in this range — connect your bank or widen the dates</p>
          </div>
        ) : (
          <BarChart weeks={weeks} />
        )}
      </div>

      {/* Where the money went */}
      {(cashflow?.by_category || []).length > 0 && (
        <div className="card p-5">
          <h2 className="text-sm font-black uppercase tracking-tight text-gray-900 mb-3">By category · {rangeLabel}</h2>
          <div className="flex flex-wrap gap-2">
            {cashflow.by_category.slice(0, 12).map((c: any) => (
              <button key={c.category} onClick={() => setTxSearch(catLabel(c.category))}
                className="text-xs px-2.5 py-1.5 rounded-full border border-gray-200 bg-white hover:border-brand-300 capitalize">
                {catLabel(c.category)}
                {c.expenses > 0 && <span className="text-red-600 font-semibold"> −{fmtDollars(c.expenses)}</span>}
                {c.income > 0 && <span className="text-green-700 font-semibold"> +{fmtDollars(c.income)}</span>}
                <span className="text-gray-500"> · {c.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* The transactions themselves */}
      {allTx.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-black uppercase tracking-tight text-gray-900 flex-1">Transactions <span className="text-gray-500 font-normal">· {txRows.length} of {allTx.length}{cashflow?.pending_count ? ` · ${cashflow.pending_count} pending` : ''}</span></h2>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
              {(['all', 'in', 'out'] as const).map(k => (
                <button key={k} onClick={() => setTxKind(k)} className={`press px-3 py-1.5 font-medium ${txKind === k ? 'bg-brand-600 text-black' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  {k === 'all' ? 'All' : k === 'in' ? 'Money in' : 'Money out'}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input value={txSearch} onChange={e => setTxSearch(e.target.value)} placeholder="Search name, category, amount…" className="input pl-8 py-1.5 text-xs w-56" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-semibold">Date</th>
                  <th className="px-4 py-2 font-semibold">Name</th>
                  <th className="px-4 py-2 font-semibold">Category</th>
                  <th className="px-4 py-2 font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {txRows.slice(0, 400).map((t: any) => {
                  const amt = Number(t.amount) || 0;
                  return (
                    <tr key={t.id} className={t.pending ? 'opacity-60' : ''}>
                      <td className="px-4 py-2 whitespace-nowrap text-gray-600">{t.date}</td>
                      <td className="px-4 py-2 text-gray-900">{t.name}{t.pending ? <span className="ml-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">pending</span> : null}</td>
                      <td className="px-4 py-2 text-gray-500 capitalize">{catLabel(t.category)}</td>
                      <td className={`px-4 py-2 text-right font-semibold whitespace-nowrap ${amt < 0 ? 'text-green-700' : 'text-gray-900'}`}>{amt < 0 ? '+' : '−'}{fmtDollars(Math.abs(amt))}</td>
                    </tr>
                  );
                })}
                {txRows.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">No transactions match.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice income (always available) */}
      {cashflow?.invoice_income?.length > 0 && (
        <div className="card p-5">
          <h2 className="text-sm font-black uppercase tracking-tight text-gray-900 mb-3">Recent Invoice Payments</h2>
          <div className="space-y-1">
            {cashflow.invoice_income.slice(-10).reverse().map((row: any, i: number) => (
              <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-gray-50 last:border-0">
                <span className="text-gray-500">{row.date}</span>
                <span className="font-medium text-green-700">{fmtDollars(Number(row.amount))}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
